import {
  Alert,
  Button,
  CardContent,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query';
import _ from 'lodash';
import React, { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form-mui';
import AnimateHeight from '../../../common/AnimateHeight';
import ListSkeleton from '../../../common/ListSkeleton';
import { getError } from '../../../helpers/checkout';
import { Address } from '../../../models/Address';
import { FulfillmentMethodType } from '../../../models/FulfillmentMethod';
import {
  AddressSource,
  FulfillmentQuoteMethod,
  GetFulfillmentQuoteParams,
} from '../../../models/FulfillmentQuote';
import { Fulfillment, OrderType } from '../../../models/Order';
import { useCreateFulfillmentQuoteQuery } from '../../../redux/api/checkoutApi';
import { useAppSelector } from '../../../redux/hooks';
import {
  selectDebug,
  selectFulfillments,
  selectParams,
  selectSessionId,
} from '../../../redux/selectors/checkoutSelectors';
import PassiveRow from '../PassiveRow';
import FulfillmentQuoteMethodList from './FulfillmentQuoteMethodList';
import useAddressFormValues from './useAddressFormValues';

interface FulfillmentQuoteContentProps {
  index: number;
  label: string;
  type: FulfillmentMethodType;
  error?: string;
  submitLoading: boolean;
}

export default function FulfillmentQuoteContent({
  index,
  label,
  type,
  error,
  submitLoading,
}: FulfillmentQuoteContentProps) {
  const theme = useTheme();
  const xsDisplay = useMediaQuery(theme.breakpoints.only('xs'));
  const params = useAppSelector(selectParams);
  const sessionId = useAppSelector((state) => selectSessionId(state, params));
  const [quoteParams, setQuoteParams] = React.useState<GetFulfillmentQuoteParams | null>(null);
  const debug = useAppSelector(selectDebug);
  const fulfillments = useAppSelector((state) => selectFulfillments(state, params));
  const preferredFulfillment = fulfillments?.filter((f) => f.method.type === type)?.[0];

  const {
    data: fulfillmentQuote,
    isLoading: fulfillmentQuoteLoading,
    error: fulfillmentQuoteError,
  } = useCreateFulfillmentQuoteQuery(quoteParams ? quoteParams : skipToken);

  const { setValue } = useFormContext<Fulfillment>();

  const method: FulfillmentQuoteMethod = useWatch({
    name: `method`,
  });

  const [savedAddressId, address, addressSource] = useAddressFormValues<
    [string, Address, AddressSource]
  >({
    names: ['savedAddressId', 'address', 'addressSource'],
  });

  useEffect(() => {
    const createQuote = async () => {
      if (sessionId && params && (type === FulfillmentMethodType.PICKUP || addressSource)) {
        setQuoteParams({
          sessionId,
          userId: params?.userId,
          type,
          addressSource,
          address: addressSource === AddressSource.USER_SUPPLIED ? _.cloneDeep(address) : undefined,
          savedAddressId,
        });
      }
    };
    createQuote();
  }, [sessionId, savedAddressId, address, addressSource, params, type, setQuoteParams]);

  useEffect(() => {
    if (fulfillmentQuote) {
      setValue(`quoteId`, fulfillmentQuote._id);
      if (preferredFulfillment) {
        setValue(`method`, preferredFulfillment.method);
      } else {
        if (fulfillmentQuote.methods.length === 1) {
          setValue(`method`, fulfillmentQuote.methods[0]);
        } else {
          const defaultMethod = fulfillmentQuote.methods.find((m) => m.isDefault);
          if (defaultMethod) {
            setValue(`method`, defaultMethod);
          }
        }
      }
    }
  }, [fulfillmentQuote, setValue, preferredFulfillment]);

  const handleSelectMethod = (method: FulfillmentQuoteMethod) => {
    setValue(`method`, method);
  };

  return (
    <>
      <Typography variant="h6" p={2}>
        Select {label} Method
      </Typography>
      {debug && (
        <Stack mx={2} mb={2}>
          <PassiveRow field="Quote ID" value={fulfillmentQuote?._id} />
          <PassiveRow
            field="Package Groups"
            value={fulfillmentQuote?.packageGroups?.length?.toString()}
          />
        </Stack>
      )}
      {!fulfillmentQuoteError && (
        <AnimateHeight>
          {fulfillmentQuote && !fulfillmentQuoteLoading && (
            <FulfillmentQuoteMethodList
              methods={fulfillmentQuote.methods}
              selectedMethodId={method?.methodId ?? null}
              onSelectMethod={handleSelectMethod}
            />
          )}
          {!fulfillmentQuote && fulfillmentQuoteLoading && (
            <ListSkeleton primaryText secondaryAction quantity={3} />
          )}
        </AnimateHeight>
      )}
      {fulfillmentQuoteError && (
        <CardContent>
          <Alert severity="error">Unable to load methods: {getError(fulfillmentQuoteError)}</Alert>
        </CardContent>
      )}
      {!fulfillmentQuoteError && (
        <CardContent>
          <Stack direction={'column'} spacing={2}>
            {error && <Alert severity="error">{error}</Alert>}
            <Stack direction={'row'} justifyContent={'center'}>
              <Button
                data-testid="fulfillment-continue-btn"
                variant="contained"
                size="large"
                loading={submitLoading}
                fullWidth={xsDisplay}
                type="submit"
              >
                {params?.orderType === OrderType.STORE_ORDER && 'Continue'}
                {params?.orderType === OrderType.QUOTE && 'Submit Quote Request'}
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      )}
    </>
  );
}
